import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plugs, Heartbeat, Table, X, ArrowRight, CheckCircle
} from '@phosphor-icons/react';

const STEPS = [
  {
    id: 'connect',
    icon: Plugs,
    title: 'Connect Your First API',
    description: 'Add an API connection to start monitoring, querying, and scheduling your integrations.',
    action: '/connections',
    actionLabel: 'Go to Connections',
  },
  {
    id: 'heatcheck',
    icon: Heartbeat,
    title: 'Run a Heat Check',
    description: 'Get an AI-powered health assessment of your API environment with actionable insights.',
    action: '/heatcheck',
    actionLabel: 'Go to Heat Check',
  },
  {
    id: 'dictionary',
    icon: Table,
    title: 'Sync the Data Dictionary',
    description: 'Auto-catalog all fields across your connected APIs for cross-system field intelligence.',
    action: '/data-dictionary',
    actionLabel: 'Go to Dictionary',
  },
];

const STORAGE_KEY = 'kova_onboarding_dismissed';
const COMPLETED_KEY = 'kova_onboarding_completed';

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setVisible(true);
    }
    const completed = JSON.parse(localStorage.getItem(COMPLETED_KEY) || '[]');
    setCompletedSteps(completed);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const handleStepAction = (step) => {
    const updated = [...new Set([...completedSteps, step.id])];
    setCompletedSteps(updated);
    localStorage.setItem(COMPLETED_KEY, JSON.stringify(updated));
    navigate(step.action);
  };

  const allComplete = STEPS.every(s => completedSteps.includes(s.id));

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-kova-card border border-kova-cyan/12 mb-6"
        data-testid="onboarding-wizard"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-kova-cyan/12">
          <div>
            <h3 className="font-heading text-sm font-bold text-kova-white">Getting Started</h3>
            <p className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mt-0.5">
              {allComplete ? 'All set! You\'re ready to go.' : `${completedSteps.length}/${STEPS.length} steps completed`}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            data-testid="onboarding-dismiss"
            className="p-1.5 text-kova-slate hover:text-kova-silv transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-0.5 bg-kova-card2">
          <motion.div
            className="h-full bg-kova-cyan"
            initial={{ width: 0 }}
            animate={{ width: `${(completedSteps.length / STEPS.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-kova-cyan/6">
          {STEPS.map((step, i) => {
            const isCompleted = completedSteps.includes(step.id);
            return (
              <div
                key={step.id}
                className={`bg-kova-card p-4 ${isCompleted ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {isCompleted ? (
                    <CheckCircle size={18} weight="fill" className="text-kova-green" />
                  ) : (
                    <step.icon size={18} className="text-kova-cyan" />
                  )}
                  <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Step {i + 1}</span>
                </div>
                <h4 className="font-heading text-sm font-bold text-kova-white mb-1">{step.title}</h4>
                <p className="text-kova-silv text-[11px] leading-relaxed mb-3">{step.description}</p>
                <button
                  data-testid={`onboarding-${step.id}`}
                  onClick={() => handleStepAction(step)}
                  className={`flex items-center gap-1 font-mono text-[9px] tracking-[2px] uppercase transition-colors ${
                    isCompleted
                      ? 'text-kova-green'
                      : 'text-kova-cyan hover:text-kova-cyan2'
                  }`}
                >
                  {isCompleted ? 'Done' : step.actionLabel}
                  {!isCompleted && <ArrowRight size={10} />}
                </button>
              </div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
